<template>
  <v-form
    ref="form"
    v-model="valid"
    @submit.prevent="onSubmit"
  >
    <v-card
      height="100%"
      flat
    >
      <v-card-title>
        <slot name="form-title">
          <span class="font-weight-semibold text-base text--primary">
            {{ $t('Ajouter une table de tarification') }}
          </span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="$emit('update:is-form-active', false)"
          >
            <v-icon size="22">{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </slot>
      </v-card-title>

      <v-card-text
        class="overflow-y-auto"
        style="max-height: calc(80vh - 120px);"
      >
        <v-row class="pt-5">
          <!-- Libellé -->
          <v-col cols="12">
            <v-text-field
              v-model="item.Label"
              outlined
              dense
              :rules="[validators.required]"
              :error-messages="errorMessages.Label"
              :label="$t('Libellé')"
              :placeholder="$t('Libellé')"
              hide-details="auto"
            />
          </v-col>

          <!-- Tranches de tarification -->
          <v-col cols="12">
            <div class="d-flex align-center mb-2">
              <span class="text-sm font-weight-semibold">{{ $t('Tranches de tarification') }}</span>
              <v-spacer />
              <v-btn
                x-small
                color="primary"
                outlined
                @click="addRow"
              >
                <v-icon x-small left>{{ icons.mdiPlus }}</v-icon>
                {{ $t('Ajouter une tranche') }}
              </v-btn>
            </div>

            <v-simple-table
              dense
              class="pricing-form-table"
            >
              <thead>
                <tr>
                  <th class="text-right">{{ $t('Pages min') }}</th>
                  <th class="text-right">{{ $t('Pages max') }}</th>
                  <th class="text-right">{{ $t('Frais (€)') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-if="pricingRows.length === 0"
                >
                  <td
                    colspan="6"
                    class="text-center text--secondary py-3"
                  >
                    {{ $t('Aucune tranche. Cliquez sur "Ajouter une tranche".') }}
                  </td>
                </tr>
                <tr
                  v-for="(row, i) in pricingRows"
                  :key="i"
                >
                  <td>
                    <v-text-field
                      v-model.number="pricingRows[i].page_min"
                      type="number"
                      dense
                      hide-details
                      class="pricing-input"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="pricingRows[i].page_max"
                      type="number"
                      dense
                      hide-details
                      class="pricing-input"
                    />
                  </td>
                  <td>
                    <v-text-field
                      v-model.number="pricingRows[i].frais"
                      type="number"
                      dense
                      hide-details
                      class="pricing-input"
                    />
                  </td>
                  <td class="text-center">
                    <v-btn
                      icon
                      x-small
                      color="error"
                      @click="removeRow(i)"
                    >
                      <v-icon x-small>{{ icons.mdiDeleteOutline }}</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <slot name="form-actions">
          <slot name="form-cancel"></slot>
          <v-spacer />
          <v-btn
            :loading="loading"
            color="primary"
            class="me-3"
            type="submit"
          >
            {{ $t('Enregistrer') }}
          </v-btn>
        </slot>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { mdiClose, mdiPlus, mdiDeleteOutline } from '@mdi/js'
import { computed, ref, watch } from '@vue/composition-api'
import { required } from '@core/utils/validation'
import store from '@/store'

const blankRow = () => ({
  page_min: null,
  page_max: null,
  frais: null,
})

export default {
  model: {
    prop: 'object',
    event: 'update:is-form-active',
  },
  props: {
    object: {
      type: Object,
      default: () => ({}),
    },
    isFormActive: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const form = ref(null)
    const isSnackbarBottomVisible = ref(false)
    const snackBarMessage = ref('')
    const errorMessages = ref({})
    const valid = ref(false)
    const loading = ref(false)

    const validate = () => {
      form.value.validate()
    }

    const item = computed({
      get: () => props.object,
      set: value => emit('update:object', value),
    })

    // Garantit que pricing est toujours un tableau éditable
    const pricingRows = computed(() => {
      const p = item.value.pricing
      if (!p) return []
      if (typeof p === 'string') {
        try { return JSON.parse(p) } catch (e) { return [] }
      }
      return p
    })

    // Si l'API renvoie pricing en string JSON, on le parse à l'ouverture
    watch(() => props.isFormActive, val => {
      if (val && item.value.pricing && typeof item.value.pricing === 'string') {
        try {
          item.value.pricing = JSON.parse(item.value.pricing)
        } catch (e) {
          item.value.pricing = []
        }
      } else if (val && !item.value.pricing) {
        item.value.pricing = []
      }
    }, { immediate: true })

    const addRow = () => {
      if (!Array.isArray(item.value.pricing)) {
        item.value.pricing = []
      }
      item.value.pricing.push(blankRow())
    }

    const removeRow = i => {
      item.value.pricing.splice(i, 1)
    }

    const onSubmit = () => {
      if (valid.value) {
        loading.value = true
        store.dispatch('pricingTable/addItem', item.value)
          .then(response => {
            loading.value = false
            if (response.data.error || response.data.errors || (response.data.status && response.data.status === 'false')) {
              if (response.data.errors) {
                errorMessages.value = { ...response.data.errors }
              } else {
                isSnackbarBottomVisible.value = true
                snackBarMessage.value = response.data.message
              }

              return false
            }
            emit('refetch-data')
          }).catch(error => {
            loading.value = false
            console.log('error', error)
            if (error.data && error.data.errors) {
              errorMessages.value = { ...error.data.errors }
            }
          })
      } else {
        validate()
      }
    }

    return {
      form,
      item,
      pricingRows,
      valid,
      loading,
      errorMessages,
      isSnackbarBottomVisible,
      snackBarMessage,
      onSubmit,
      addRow,
      removeRow,
      validate,
      validators: { required },
      icons: {
        mdiClose,
        mdiPlus,
        mdiDeleteOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.pricing-form-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;

  th {
    font-size: 0.75rem !important;
    white-space: nowrap;
  }

  td {
    padding: 4px 8px !important;
  }
}

.pricing-input {
  min-width: 80px;

  ::v-deep input {
    text-align: right;
  }
}
</style>